import dayjs from "dayjs";

/**
 * Public/government dental programs and the field values they imply.
 *
 * CDCP and the programs alongside it aren't really "carriers". Every policy under
 * one is the same shape: the member IS the patient (relation `self`, member name
 * and DOB are the patient's), the plan is primary, and every policy in the country
 * shares one contract/group number. Staff shouldn't retype that — and when they
 * do, they get it wrong: 52 of 492 CDCP policies in production had no contract
 * number at all, which silently breaks routing (the dispatcher only switches a
 * SunLife policy to the CDCP coverage form when it carries the sentinel contract).
 *
 * ⚠️ MIRRORS `clinics/services/government_plans.py`. Same arrangement as
 * `policyCompleteness.js` ↔ `policy_completeness.py`, and the same hazard: this
 * codebase has been bitten by those two copies drifting. Change one, change both.
 *
 * Only add a carrier once its contract number is confirmed from real data across
 * several offices. A wrong number doesn't fail loudly — it produces a policy that
 * looks complete and then fails at the carrier, which is worse than a blank field.
 */

// Keyed by InsuranceProvider.code — the value the carrier Select carries.
export const GOVERNMENT_PLANS = {
  CDCP: {
    label: "Canadian Dental Care Plan",
    contractNumber: "333333", // clinics/services/insurance_dispatch.py CDCP_CONTRACT_NUMBER
    insuranceType: "primary",
    relationToMember: "self",
    // The program's own facts, filled on every CDCP plan by the server where
    // nothing else answered them (government_plans.py plan_facts): Jul 1 –
    // Jun 30 year, no deductible, single coverage, pays the office directly.
    programFields: [
      "benefit_period_type", "benefit_year_start", "benefit_year_end", "deductible",
      "coverage_scope", "assignment_of_benefits",
    ],
    // "Overall maximum: None" on the portal.
    noDollarMaximum: true,
  },
  NIHB: {
    // Non-Insured Health Benefits. Evidence is thinner than CDCP's: of 21 NIHB
    // policies found across 8 practices, 18 had a number — 14 of them this one,
    // from 4 independent offices. The dissenters are explicable rather than
    // competing ("1234" a placeholder, "8080000" a transposition).
    label: "Non-Insured Health Benefits",
    contractNumber: "080000",
    insuranceType: "primary",
    relationToMember: "self",
  },

  // Known shape, unknown number. These fill relation/ordinal/member and leave the
  // contract field for a human — a sweep of 71,415 patients found no single value
  // for any of them, and a wrong contract number is worse than a blank one because
  // the policy then looks complete and fails at the carrier instead.
  // ODSP and HSO are administered by Accerta, whose "group number" names WHICH
  // program the patient is on — 4 offices each use both codes, so it tracks the
  // program, not the practice. Ages confirm which is which, with no overlap:
  // HSO median 15 (63% under 18); MCS median 50 (0% under 18).
  ODSP: {
    // ⚠️ "MCS" is the adult social-assistance stream; the data can't separate ODSP
    // from Ontario Works, so an OW patient gets this too.
    label: "Ontario Disability Support Program",
    contractNumber: "MCS",
    insuranceType: "primary",
    relationToMember: "self",
  },
  HSO: {
    // 191 policies across 4 offices. Uppercase — practices type it both ways.
    label: "Healthy Smiles Ontario",
    contractNumber: "HSO",
    insuranceType: "primary",
    relationToMember: "self",
  },
  IFHP: {
    // One policy across every office swept — far too thin to fill automatically.
    label: "Interim Federal Health Program",
    contractNumber: null,
    insuranceType: "primary",
    relationToMember: "self",
  },

  // NOT LISTED: ACCERTA. Seeded as a carrier but deliberately ungoverned — of 465
  // Accerta policies, 5 say "Spouse", and these fields overwrite, so forcing
  // relation self would replace a spouse's name and DOB with the patient's.
};

/** Fields the rule owns — overwritten on selection, never merged. */
export const GOVERNED_FIELDS = [
  "insurance_type",
  "relation_to_member",
  "first_name",
  "last_name",
  "date_of_birth",
  "contract_number",
];

export function isGovernmentPlan(carrierCode) {
  return !!(carrierCode && GOVERNMENT_PLANS[String(carrierCode).toUpperCase().trim()]);
}

/**
 * The government program a policy is under, or null — counting a CDCP plan
 * filed under Sun Life with CDCP's contract number (the server's
 * template_provider).
 */
export function programOfPolicy(policy) {
  const p = policy || {};
  const code = String(p.insurance_name || p.carrier_code || p.insurance_code || "").toUpperCase().trim();
  if (code === "SUNLIFE" && String(p.contract_number || "").trim() === GOVERNMENT_PLANS.CDCP.contractNumber) {
    return GOVERNMENT_PLANS.CDCP;
  }
  return GOVERNMENT_PLANS[code] || null;
}

export function planFor(carrierCode) {
  if (!carrierCode) return null;
  return GOVERNMENT_PLANS[String(carrierCode).toUpperCase().trim()] || null;
}

/**
 * The antd form values `carrierCode` implies for `patient`.
 *
 * Returns `{}` for a commercial carrier so callers can spread it unconditionally.
 * `date_of_birth` comes back as a dayjs object because that is what the form's
 * DatePicker binds to; `contract_number` is omitted entirely when the program's
 * number isn't known yet, so we never blank out a real value with a placeholder.
 *
 * @param {string} carrierCode  InsuranceProvider.code, e.g. "CDCP"
 * @param {Object} patient      needs first_name / last_name / birth_date
 */
export function governmentPlanValues(carrierCode, patient) {
  const plan = planFor(carrierCode);
  if (!plan) return {};

  const values = {
    insurance_type: plan.insuranceType,
    relation_to_member: plan.relationToMember,
  };
  if (plan.contractNumber) values.contract_number = plan.contractNumber;

  const p = patient || {};
  values.first_name = p.first_name || "";
  values.last_name = p.last_name || "";

  const dob = p.birth_date || p.date_of_birth;
  if (dob) {
    const parsed = dayjs(typeof dob === "string" ? dob.split("T")[0] : dob);
    if (parsed.isValid()) values.date_of_birth = parsed;
  }

  return values;
}
